<template>
  <b-modal
    id="apply-to-run-modal"
    ref="apply-to-run-modal"
    size="lg"
    header-class="custom-modal"
    body-class="custom-modal"
    footer-class="custom-modal"
    :hide-footer="true"
    @hidden="onModalHidden"
    @shown="onModalShown"
  >
    <template #modal-title>
      <b-container>
        <b-row align-h="end">
          <b-col>
            Apply to Run: {{ offer.name }} 
          </b-col>
          <b-col cols="2">
            <b-badge 
              :variant="getBadgeVariant(offer.status)"
              pill 
              right-align
            >
              {{ offer.status }}
            </b-badge>
          </b-col>
        </b-row>
      </b-container>
    </template>
    <div class="px-4">
      <div
        v-if="showInsights && loadingInsights"
        class="mb-3"
      >
        <Loader />
      </div>
      <div
        v-else-if="showInsights && offerInsights"
        class="mb-3"
      >
        <InsightsDisplay :insights="offerInsights" />
        <hr>
      </div>

      <validation-observer
        ref="formValidation"
        v-slot="{ handleSubmit, invalid }"
      >
        <b-form
          novalidate
          @submit.stop.prevent="handleSubmit(submitForm)"
        >
          <!-- Webhook Configuration Section - Must be first for CPA offers -->
          <div
            v-if="isCpaOnlyCategory"
            class="mb-4"
          >
            <h5 class="mb-3">
              Webhook Configuration

              <small
                class="text-danger ml-2"
              >
                <i>Required</i>
              </small>
            </h5>

            <b-alert
              show
              variant="info"
              class="mb-3 py-2"
            >
              <font-awesome-icon
                :icon="['fas', 'info-circle']"
                class="mr-2"
              />
              <strong>CPA Category:</strong> This offer uses Cost Per Acquisition (CPA) billing. Payouts are based on conversion events, not call duration.
            </b-alert>

            <!-- Ringba Integration Required Alert -->
            <CpaWebhookRequirementAlert
              v-if="requiresRingbaIntegration"
              class="mb-3"
            />

            <!-- Webhook Selection Section -->
            <CpaWebhookSelector
              v-if="accountHasRingbaIntegrationCompleted"
              :account-id="accountId"
            />

            <b-alert
              v-if="accountHasRingbaIntegrationCompleted && !hasWebhookConfigured"
              show
              variant="warning"
              class="mb-0 py-2"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
                class="mr-2"
              />
              Please select and save a webhook before submitting your application.
            </b-alert>

            <hr>
          </div>

          <!-- Offer Details Section -->
          <BaseFormInput
            id="seller-offer__description"
            v-model="offer.description"
            :horizontal="true"
            :small="true"
            internal-platform-input
            label="Description"
            type="text"
            width-lg="8"
            width-xl="8"
            has-tooltip
            plaintext
            input-class="px-0"
          />

          <BaseFormSelect
            id="seller-offer__language"
            v-model="languageId"
            :horizontal="true"
            internal-platform-input
            :small="true"
            name="Language"
            label="Language"
            text-field="name"
            value-field="id"
            :options="languageList"
            required
            :disabled="true"
            width="5"
            width-lg="4"
            width-xl="3"
            has-tooltip
          />

          <BaseFormSelect
            id="seller-offer__category"
            v-model="categoryId"
            :horizontal="true"
            internal-platform-input
            :small="true"
            name="Category"
            label="Category"
            text-field="name"
            value-field="id"
            :options="categoryList"
            required
            :disabled="true"
            width="5"
            width-lg="4"
            width-xl="3"
            has-tooltip
          />

          <BaseFormSelect
            id="seller-offer__traffic-source-type"
            v-model="allowedTrafficSourceType"
            :horizontal="true"
            internal-platform-input
            :small="true"
            name="Traffic Source Type"
            label="Traffic Source Type"
            :options="allowedTrafficSourceTypeList"
            required
            :disabled="true"
            width="5"
            width-lg="4"
            width-xl="3"
            has-tooltip
          />

          <hr>

          <validation-provider
            name="input-traffic-sources"
            rules="required"
          >
            <b-form-group
              label="Traffic Sources"
              label-for="input-traffic-sources"
              label-class="custom-form-input-label--internal"
              :invalid-feedback="invalidFeedback"
              :state="state"
            >
              <template
                slot="label"
              >
                <div>
                  Traffic Sources
                  <span
                    v-b-tooltip.hover.right
                    :title="'The types of promotional methods allowed for this offer'"
                  >
                    <font-awesome-icon
                      class="ml-1 mt-1"
                      :icon="['fas', 'question-circle']"
                    />
                  </span>
                </div>
              </template>
              <v-multiselect-listbox
                id="input-traffic-sources"
                v-model="selectedTrafficSources"
                class="msl-multi-select--full-width"
                :options="allowedTrafficSources"
                :reduce-display-property="option => option.name"
                :reduce-value-property="option => option.id"
                search-input-class="custom-form-input custom-form-input--sm"
                search-options-placeholder="Search traffic source"
                selected-options-placeholder="Search selected"
                selected-no-options-text="No source selected"
                :show-select-all-buttons="true"
              />
            </b-form-group>
          </validation-provider>

          <BaseButtonGroup
            internal-platform-input
            :selected-option="thirdPartyTraffic"
            :click-handler="selectAnswerForThirdPartyTraffic"
            label="Will any of your traffic come from third party affiliates?"
            :options="['Yes', 'No', 'Some']"
          />

          <BaseFormTextArea
            id="landingPageURLs"
            v-model="landingPageURLs"
            internal-platform-input
            :small="true"
            rows="4"
            data-cy="input-landing-page-urls"
            name="input-landing-page-urls"
            label="Please provide URLs for landing pages if applicable:"
            placeholder=""
          />

          <b-form-group
            :invalid-feedback="promotionalMethodsDetailsInvalidFeedback"
            :state="promotionalMethodsDetailsState"
          >
            <BaseFormTextArea
              id="promotionalMethodsDetails"
              v-model="promotionalMethodsDetails"
              internal-platform-input
              :small="true"
              rows="4"
              label="Please provide specific details regarding your promotional methods:"
              placeholder=""
            />
          </b-form-group>

          <hr>

          <b-form-group
            label="Creative Files"
            label-for="creatives-upload"
            label-class="custom-form-input-label--internal"
            :invalid-feedback="creativesInvalidFeedback"
            :state="creativesState || null"
          >
            <template slot="label">
              <div>
                Creative Files
                <span
                  v-b-tooltip.hover.right
                  :title="'Upload creative media files (audio, picture, video, or PDF) and specify the traffic source for each'"
                >
                  <font-awesome-icon
                    class="ml-1 mt-1"
                    :icon="['fas', 'question-circle']"
                  />
                </span>
              </div>
            </template>

            <div>
              <div
                v-if="isMedicareCategory"
                class="text-muted"
              >
                Medicare creatives must be uploaded separately
              </div>

              <b-button
                v-else-if="showAddCreativeButton"
                type="button"
                size="sm"
                @click="addCreativeInput"
              >
                Add creative file
              </b-button>

              <div v-if="!showAddCreativeButton && !isMedicareCategory">
                <div
                  v-for="(creative, idx) in creatives"
                  :key="idx"
                  class="mb-3"
                >
                  <div class="form-row align-items-start">
                    <div class="col-12 col-sm-4">
                      <b-form-select
                        :id="`creative-traffic-source-${idx}`"
                        v-model="creative.trafficSourceId"
                        class="form-control custom-form-input--xs"
                        size="sm"
                        :options="trafficSourceOptions"
                        :disabled="creative.uploaded"
                        @input="updateCreativeTrafficSource(idx, $event)"
                      >
                        <template #first>
                          <b-form-select-option
                            :value="''"
                            disabled
                          >
                            Select traffic source
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </div>

                    <div class="col-12 col-sm-4 mt-2 mt-sm-0">
                      <b-form-file
                        v-if="!creative.isAdditionalFile"
                        v-model="creative.file"
                        accept=".jpg,.png,.jpeg,.mp4,.mov,.pdf,.avi,.mp3,.wav"
                        size="sm"
                        placeholder="Choose file(s)"
                        multiple
                        :disabled="!creative.trafficSourceId"
                        @input="handleCreativeFileSelected(idx)"
                      />
                      <div
                        v-else
                        class="form-control form-control-sm"
                        style="background-color: #e9ecef; cursor: not-allowed; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                        :title="creative.fileName"
                      >
                        {{ creative.fileName }}
                      </div>
                    </div>

                    <div class="col-auto">
                      <b-button
                        type="button"
                        class="ml-2 bg-transparent border-0"
                        :disabled="creative.progress > 0 && creative.progress < 100 && !creative.error"
                        @click="removeCreativeInput(idx)"
                      >
                        <font-awesome-icon :icon="['fas', 'minus-circle']" />
                      </b-button>
                      <b-button
                        v-if="idx === creatives.length - 1"
                        type="button"
                        class="ml-2 bg-transparent border-0"
                        @click="addCreativeInput"
                      >
                        <font-awesome-icon :icon="['fas', 'plus-circle']" />
                      </b-button>
                      <b-button
                        v-if="creative.error"
                        type="button"
                        class="ml-2 bg-transparent border-0"
                        @click="retryCreativeUpload(idx)"
                      >
                        <font-awesome-icon :icon="['fas', 'sync']" />
                      </b-button>
                    </div>
                  </div>

                  <div
                    v-if="creative.progress > 0 && creative.progress < 100 && !creative.uploaded"
                    class="form-row mt-2"
                  >
                    <div class="col-12 col-sm-8">
                      <b-progress
                        :value="creative.progress"
                        :max="100"
                        animated
                      />
                      <small>File size {{ formatFileSize(creative.file.size) }}</small>
                      <div v-if="creative.error">
                        <small class="text-danger">
                          <p>
                            Upload failed <br>
                            {{ creative.error }}
                          </p>
                        </small>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="creative.uploaded"
                    class="form-row mt-1"
                  >
                    <div class="col-12">
                      <small class="text-success">
                        <font-awesome-icon :icon="['fas', 'check']" />
                        {{ creative.fileName }} uploaded successfully
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-form-group>

          <hr>

          <BaseFormCheckbox
            v-if="isCpaOnlyCategory"
            id="acceptedCpaTerms"
            v-model="acceptedCpaTerms"
            name="acceptedCpaTerms"
            required
            require-checked
          >
            I understand that this is a <strong>CPA (Cost Per Acquisition)</strong> offer.
            Payouts will only be made when conversion events are confirmed, not based on
            call duration or connection status.
          </BaseFormCheckbox>

          <BaseFormCheckbox
            id="acceptedTermsAndConditions"
            v-model="acceptedTermsAndConditions"
            name="acceptedTermsAndConditions"
            required
            require-checked
          >
            I understand that misrepresentation of traffic sources or the
            failure to segment third party traffic will result in termination
            from Ringba X and may result in non-payment in accordance with the
            Ringba X Terms of Service
          </BaseFormCheckbox>

          <BaseButtonLoader
            class="mt-4"
            data-cy="button-create-campaign"
            internal-platform-input
            :disabled="invalid || !isFormValid"
            variant="primary"
            :loading="loading"
            type="submit"
          >
            Apply
          </BaseButtonLoader>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import { formatFileSize } from '@/utils/formatFileSize'
import InsightsDisplay from './InsightsDisplay.vue'
import CpaWebhookRequirementAlert from './CpaWebhookRequirementAlert.vue'
import CpaWebhookSelector from './CpaWebhookSelector.vue'

export default {
  name: 'SellerApplyToRunModal',

  components: {
    InsightsDisplay,
    CpaWebhookRequirementAlert,
    CpaWebhookSelector
  },
  props: {
    offer: {
      type: Object,
      default: () => {}
    },
    showInsights: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedTrafficSources: [],
      thirdPartyTraffic: 'No',
      landingPageURLs: null,
      promotionalMethodsDetails: null,
      acceptedTermsAndConditions: false,
      acceptedCpaTerms: false,
      loading: false,
      creatives: [],
      loadingInsights: false,
    }
  },
  computed:{
    ...mapState('creative', ['creativeFiles']),

    ...mapGetters('insights', [
      'getInsightsByOfferId'
    ]),

    ...mapGetters('account', [
      'accountHasRingbaIntegrationCompleted'
    ]),

    ...mapGetters('sellerWebhooks', [
      'hasWebhookConfigured'
    ]),

    ...mapGetters('auth', ['accountId']),

    offerInsights() {
      const sellerOfferId = this.offer?.id
      return this.getInsightsByOfferId(sellerOfferId)
    },

    state() {
      return this.selectedTrafficSources.length > 0
    },

    invalidFeedback() {
      if (this.selectedTrafficSources.length === 0) {
        return 'Please select at least one traffic source.'
      }
      return 'Please select traffic sources.'
    },

    promotionalMethodsDetailsState() {
      return this.promotionalMethodsDetails != null
    },

    promotionalMethodsDetailsInvalidFeedback() {
      return 'Please enter promotional methods details.'
    },

    creativesState() {
      // Medicare categories don't require creative files
      if (this.isMedicareCategory) {
        return true
      }
      return this.creatives.length > 0 && this.creatives.every(c => c.uploaded && !c.error && c.fileId)
    },

    creativesInvalidFeedback() {
      return 'Please upload at least one creative file.'
    },

    categoryList() {
      const categoryList = [this.offer.category]
      return categoryList
    },

    languageList() {
      return [this.offer.language]
    },

    languageId: {
      get() {
        return this.offer.language.id
      }
    },

    categoryId: {
      get() {
        return this.offer.category.id
      }
    },

    allowedTrafficSourceTypeList() {
      const categoryList = [this.offer.allowedTrafficSourceType]
      return categoryList
    },

    allowedTrafficSourceType: {
      get() {
        return this.offer.allowedTrafficSourceType
      }
    },

    allowedTrafficSources: {
      get() {
        return this.offer.allowedTrafficSources
      }
    },

    showAddCreativeButton() {
      return !this.creatives.length
    },

    trafficSourceOptions() {
      // Show all traffic sources but disable the ones not selected
      // selectedTrafficSources is an array of IDs due to reduce-value-property
      const selectedIds = this.selectedTrafficSources || []

      // Map all allowed traffic sources to options
      // Disable those that are not selected (uploaded creatives will have disabled dropdown anyway)
      return this.allowedTrafficSources.map(ts => ({
        value: ts.id,
        text: ts.name,
        disabled: !selectedIds.includes(ts.id)
      }))
    },

    isMedicareCategory() {
      return this.offer.category?.isMedicare || false
    },

    isCpaOnlyCategory() {
      return this.offer.category?.isCpaConversionOnly || false
    },

    /**
     * Returns true if the offer requires webhook selection (CPA category + Ringba integration complete)
     */
    requiresWebhookSelection() {
      return this.isCpaOnlyCategory && this.accountHasRingbaIntegrationCompleted && !this.hasWebhookConfigured
    },

    /**
     * Returns true if CPA category requires Ringba integration but it's not complete
     */
    requiresRingbaIntegration() {
      return this.isCpaOnlyCategory && !this.accountHasRingbaIntegrationCompleted
    },

    /**
     * Returns true if the webhook step is complete (not needed or already configured)
     */
    isWebhookStepComplete() {
      if (!this.isCpaOnlyCategory) {
        return true
      }
      if (!this.accountHasRingbaIntegrationCompleted) {
        return false
      }
      return this.hasWebhookConfigured
    },

    isFormValid() {
      // CPA terms must be accepted if category is CPA-only
      const cpaTermsValid = this.isCpaOnlyCategory ? this.acceptedCpaTerms : true
      // Webhook must be configured for CPA categories (if Ringba integration is complete)
      const webhookValid = this.isWebhookStepComplete
      return this.acceptedTermsAndConditions && cpaTermsValid && webhookValid
    },
  },

  destroyed() {
    this.RESET_CREATIVE_FILES()
  },

  methods: {
    ...mapActions('sellerOffer', ['submitParticipationRequest']),
    ...mapActions('creative', ['uploadFile']),
    ...mapActions('insights', ['getSellerOfferInsights']),
    ...mapMutations('creative', ['RESET_CREATIVE_FILES', 'REMOVE_CREATIVE_FILE']),

    formatFileSize,

    onModalHidden() {
      this.$emit('hidden')
    },

    async onModalShown() {
      this.$emit('shown')
      await this.fetchInsights()
    },

    async fetchInsights() {
      if (!this.showInsights) {
        return
      }

      const sellerOfferId = this.offer?.id
      if (!sellerOfferId) {
        return
      }

      // First check if insights are in cache
      const cachedInsights = this.getInsightsByOfferId(sellerOfferId)
      if (cachedInsights) {
        return
      }

      // If not in cache, fetch individually
      try {
        this.loadingInsights = true
        await this.getSellerOfferInsights(sellerOfferId)
      } catch {
        // Silently handle errors - insights section will not show
      } finally {
        this.loadingInsights = false
      }
    },

    selectAnswerForThirdPartyTraffic(option) {
      this.thirdPartyTraffic = option
    },

    getBadgeVariant(status) {
      switch (status) {
      case 'Disabled':
        return 'warning'
      default:
        return 'success'
      }
    },

    async uploadNewCreative(event) {
      if (!event || !event.list) {
        return
      }

      const creative = this.creatives[event.index]

      if (!creative.trafficSourceId) {
        this.creatives[event.index].error = 'Please select a traffic source'
        showErrorMessage('Please select a traffic source for the creative')
        return
      }

      try {
        const responseData = await this.uploadFile({
          request: {
            file: event.list[event.index].file,
            index: event.index,
            isApprovalDoc: false
          },
          progressCallback: this.creativeUploadProgress
        })

        this.creatives[event.index].fileId = responseData
        this.creatives[event.index].uploaded = true
        this.creatives[event.index].progress = 0
        this.creatives[event.index].error = ''

        showSuccessMessage('Successfully uploaded ' + this.creatives[event.index].fileName)
      } catch (error) {
        this.creatives[event.index].error = error
        showErrorMessage(error)
      }
    },

    creativeRemoved(fileId) {
      this.REMOVE_CREATIVE_FILE(fileId)
    },

    creativeUploadProgress(value) {
      if (this.creatives[value.index]) {
        this.creatives[value.index].progress = parseInt(Math.round(value.progress))
      }
    },

    updateCreativeTrafficSource(index, trafficSourceId) {
      if (this.creatives[index]) {
        this.creatives[index].trafficSourceId = trafficSourceId
      }
    },

    addCreativeInput() {
      // Pre-select traffic source from last creative if available
      const lastCreative = this.creatives.length > 0 ? this.creatives[this.creatives.length - 1] : null
      const preSelectedTrafficSourceId = lastCreative?.trafficSourceId || ''

      this.creatives.push({
        fileId: '',
        fileName: '',
        file: null,
        uploaded: false,
        progress: 0,
        error: '',
        trafficSourceId: preSelectedTrafficSourceId
      })
    },

    removeCreativeInput(idx) {
      const removedCreative = this.creatives[idx]

      if (removedCreative.uploaded && removedCreative.fileId) {
        this.REMOVE_CREATIVE_FILE(removedCreative.fileId)
      }

      this.creatives.splice(idx, 1)
    },

    handleCreativeFileSelected(index) {
      const creative = this.creatives[index]

      if (creative.uploaded && creative.fileId) {
        this.REMOVE_CREATIVE_FILE(creative.fileId)
      }

      if (creative.file != null) {
        // Handle multiple file selection
        const files = Array.isArray(creative.file) ? creative.file : [creative.file]

        if (files.length === 0) {
          return
        }

        // Get the traffic source from the current creative
        const trafficSourceId = creative.trafficSourceId

        // Update the first creative with the first file
        creative.file = files[0]
        creative.fileName = files[0].name
        creative.progress = 0
        creative.uploaded = false

        this.uploadNewCreative({
          list: this.creatives,
          index: index
        })

        // Create additional creative entries for remaining files
        for (let i = 1; i < files.length; i++) {
          const newCreative = {
            fileId: '',
            fileName: files[i].name,
            file: files[i],
            uploaded: false,
            progress: 0,
            error: '',
            trafficSourceId: trafficSourceId,
            isAdditionalFile: true // Flag to indicate this was added from multi-select
          }

          this.creatives.push(newCreative)

          // Upload the new creative
          const newIndex = this.creatives.length - 1
          this.uploadNewCreative({
            list: this.creatives,
            index: newIndex
          })
        }
      }
    },

    retryCreativeUpload(index) {
      this.creatives[index].error = ''
      this.creatives[index].progress = 0
      this.creatives[index].uploaded = false

      this.uploadNewCreative({
        list: this.creatives,
        index: index
      })
    },

    async submitForm() {
      if (!this.isFormValid) {
        return
      }

      // Only validate creatives for non-Medicare categories
      if (!this.isMedicareCategory) {
        if (!this.creatives.every(c => c.uploaded && !c.error)) {
          showErrorMessage('Please ensure all creatives are uploaded successfully')
          return
        }

        if (this.creatives.length === 0) {
          showErrorMessage('Please upload at least one creative')
          return
        }
      }

      try {
        this.loading = true

        // Map creatives to include fileId, trafficSourceId, fileName, and trafficSourceName
        const creativeFiles = this.creatives.map(creative => {
          const trafficSource = this.allowedTrafficSources.find(ts => ts.id === creative.trafficSourceId)
          return {
            fileId: creative.fileId,
            trafficSourceId: creative.trafficSourceId,
            trafficSourceName: trafficSource?.name || '',
            fileName: creative.fileName
          }
        })

        const payload = {
          sellerOfferId: this.offer.id,
          selectedTrafficSources: this.selectedTrafficSources,
          thirdPartyTraffic: this.thirdPartyTraffic,
          landingPageURLs: this.landingPageURLs,
          promotionalMethodsDetails: this.promotionalMethodsDetails,
          creativeFiles: creativeFiles,
        }
        await this.submitParticipationRequest(payload)
        this.disabled = true
        showSuccessMessage("Successfuly submitted your application request")
        this.$emit('success')
        setTimeout(() => {
          this.$bvModal.hide('apply-to-run-modal')
        })
      } catch (error) {
        showErrorMessage(`${error.response?.data?.title} ${JSON.stringify(error.response?.data?.errors) ?? ''}`)
      } finally {
        this.loading = false
      }
    },
  }
}
</script>

<style lang="scss">

</style>
